import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from "@/components/ui/alert-dialog";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Plus, Edit, Trash2, Eye, Calendar, ArrowLeft, MessageSquare, Upload, X } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { format } from "date-fns";
import RichTextEditor from "@/components/RichTextEditor";
import PopupManager from "@/components/PopupManager";
import { Link, useNavigate } from "react-router-dom";

interface Popup {
  id: string;
  title: string;
  content: string;
  is_active: boolean;
  button_text: string;
  button_url?: string;
  featured_image?: string;
  display_on: 'home' | 'blog' | 'both';
  created_at: string;
  updated_at?: string;
}

interface BlogPost {
  id: string;
  title: string;
  slug: string;
  content: string;
  excerpt: string;
  published: boolean;
  published_at: string;
  tags: string[];
  featured_image: string;
  view_count: number;
  created_at: string;
}

const AdminBlog = () => {
  const [posts, setPosts] = useState<BlogPost[]>([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPost, setEditingPost] = useState<BlogPost | null>(null);
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [credentials, setCredentials] = useState({ username: '', password: '' });
  const { toast } = useToast();
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    content: '',
    excerpt: '',
    published: false,
    tags: '',
    featured_image: ''
  });
  const [isUploading, setIsUploading] = useState(false);
  const [popups, setPopups] = useState<Popup[]>([]);

  const fetchPopups = async () => {
    try {
      const { data, error } = await supabase
        .from('popups')
        .select('*')
        .order('created_at', { ascending: false });
      
      if (error) throw error;
      
      // Ensure all required fields are present and properly typed
      const typedPopups: Popup[] = (data || []).map((popup: any) => {
        // Safely get display_on with type checking
        const displayOn = (popup.display_on && ['home', 'blog', 'both'].includes(popup.display_on))
          ? popup.display_on as 'home' | 'blog' | 'both'
          : 'home';
          
        return {
          id: popup.id,
          title: popup.title || '',
          content: popup.content || '',
          is_active: Boolean(popup.is_active),
          button_text: popup.button_text || 'Tutup',
          button_url: popup.button_url,
          featured_image: popup.featured_image,
          display_on: displayOn,
          created_at: popup.created_at || new Date().toISOString(),
          updated_at: popup.updated_at
        };
      });
      
      setPopups(typedPopups);
    } catch (error) {
      console.error('Error fetching popups:', error);
      toast({
        title: "Error",
        description: "Gagal memuat data popup",
        variant: "destructive",
      });
    }
  };

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        const adminAuth = localStorage.getItem('adminAuth');
        
        if (session || adminAuth === 'true') {
          setIsAuthenticated(true);
          fetchPosts();
        } else {
          setLoading(false);
        }
      } catch (error) {
        console.error('Auth check error:', error);
        setLoading(false);
      }
    };
    
    checkAuth();
    
    if (isAuthenticated) {
      fetchPopups();
    }
    
    // Listen for auth state changes
    const { data: { subscription } } = supabase.auth.onAuthStateChange((event, session) => {
      if (event === 'SIGNED_IN') {
        setIsAuthenticated(true);
        localStorage.setItem('adminAuth', 'true');
        fetchPosts();
        fetchPopups();
      } else if (event === 'SIGNED_OUT') {
        setIsAuthenticated(false);
        localStorage.removeItem('adminAuth');
      }
    });
    
    return () => {
      subscription?.unsubscribe();
    };
  }, []);

  // Handle ketika tombol Enter ditekan
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      handleLogin();
    }
  };

  const handleLogin = async () => {
    try {
      // Cek apakah input adalah email yang valid
      const email = credentials.username.includes('@') 
        ? credentials.username 
        : `${credentials.username}@perkalian.com`;
      
      console.log('Attempting login with:', email);
      
      // Coba login dengan email dan password
      const { data, error } = await supabase.auth.signInWithPassword({
        email: email,
        password: credentials.password || 'default_password', // Gunakan password default jika kosong
      });

      if (error) {
        console.error('Login error details:', error);
        throw error;
      }
      
      if (data?.user) {
        console.log('Login successful, user:', data.user);
        setIsAuthenticated(true);
        localStorage.setItem('adminAuth', 'true');
        fetchPosts();
        toast({
          title: "Login Berhasil",
          description: `Selamat datang, ${data.user.email || 'Admin'}`,
        });
      } else {
        throw new Error('Autentikasi gagal: Data pengguna tidak ditemukan');
      }
    } catch (error) {
      console.error('Login error:', error);
      
      // Tampilkan pesan error yang lebih spesifik
      let errorMessage = 'Terjadi kesalahan saat login';
      
      if (error instanceof Error) {
        if (error.message.includes('Invalid login credentials')) {
          errorMessage = 'Email atau password salah';
        } else if (error.message.includes('Email not confirmed')) {
          errorMessage = 'Email belum dikonfirmasi. Silakan periksa email Anda.';
        } else {
          errorMessage = error.message;
        }
      }
      
      toast({
        title: "Login Gagal",
        description: errorMessage,
        variant: "destructive",
      });
    }
  };

  const handleLogout = async () => {
    try {
      await supabase.auth.signOut();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      setIsAuthenticated(false);
      localStorage.removeItem('adminAuth');
      navigate('/');
    }
  };

  const fetchPosts = async () => {
    try {
      const { data, error } = await supabase
        .from('blog_posts')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setPosts(data || []);
    } catch (error) {
      console.error('Error fetching posts:', error);
      toast({
        title: "Error",
        description: "Gagal memuat artikel",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title: '',
      slug: '',
      content: '',
      excerpt: '',
      published: false,
      tags: '',
      featured_image: ''
    });
    setEditingPost(null);
    setIsUploading(false);
  };

  // Fungsi untuk memeriksa dan membuat bucket jika belum ada
  const ensureBucketExists = async () => {
    const bucketName = 'blog-images';
    
    try {
      console.log('Memeriksa ketersediaan bucket:', bucketName);
      
      // Coba akses bucket terlebih dahulu
      const { data, error: accessError } = await supabase.storage
        .from(bucketName)
        .list();
      
      // Jika berhasil mengakses, bucket sudah ada
      if (!accessError) {
        console.log('Bucket sudah ada dan dapat diakses:', bucketName);
        return true;
      }
      
      console.log('Mencoba membuat bucket baru:', bucketName);
      
      // Coba buat bucket
      const { error: createError } = await supabase.storage.createBucket(bucketName, {
        public: true,
        allowedMimeTypes: ['image/*'],
        fileSizeLimit: 5 * 1024 * 1024 // 5MB
      });
      
      if (createError) {
        console.error('Gagal membuat bucket:', createError);
        // Coba buat bucket dengan opsi minimal jika gagal
        const { error: simpleCreateError } = await supabase.storage.createBucket(bucketName, {
          public: true
        });
        
        if (simpleCreateError) {
          console.error('Gagal membuat bucket dengan opsi minimal:', simpleCreateError);
          throw new Error(`Gagal membuat bucket: ${simpleCreateError.message}`);
        }
      }
      
      console.log('Bucket berhasil dibuat:', bucketName);
      
      // Setel kebijakan akses publik
      try {
        const { error: policyError } = await supabase.storage
          .from(bucketName)
          .createSignedUrl('test.txt', 60); // Coba buat URL signed
          
        if (policyError) {
          console.warn('Peringatan: Gagal membuat URL signed, namun upload mungkin tetap berhasil:', policyError);
        }
      } catch (policyError) {
        console.warn('Peringatan: Gagal mengatur kebijakan akses:', policyError);
      }
      
      return true;
    } catch (error) {
      console.error('Error dalam ensureBucketExists:', error);
      // Lanjutkan meskipun ada error, mungkin bucket sudah ada
      return true;
    }
  };

  const handleImageUpload = async (file: File) => {
    if (!file) return;
    
    console.log('Memulai unggah file:', file.name, 'Tipe:', file.type, 'Ukuran:', file.size);
    
    // Validasi tipe file
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.some(type => file.type === type)) {
      const errorMsg = `Tipe file tidak didukung: ${file.type}. Format yang didukung: ${allowedTypes.join(', ')}`;
      console.error('Tipe file tidak valid:', file.type);
      toast({
        title: "Error",
        description: errorMsg,
        variant: "destructive",
      });
      return;
    }

    // Validasi ukuran file (maksimal 5MB)
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) {
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
      const errorMsg = `Ukuran file terlalu besar: ${fileSizeMB}MB. Maksimal 5MB`;
      console.error('File terlalu besar:', file.size, 'bytes');
      toast({
        title: "Error",
        description: errorMsg,
        variant: "destructive",
      });
      return;
    }

    setIsUploading(true);
    
    try {
      // Cek status autentikasi dengan cara yang lebih baik
      const { data: { user }, error: authError } = await supabase.auth.getUser();
      
      if (authError || !user) {
        console.error('Error autentikasi:', authError);
        // Coba refresh session
        const { data: { session }, error: sessionError } = await supabase.auth.getSession();
        
        if (sessionError || !session) {
          console.error('Gagal mendapatkan sesi:', sessionError);
          // Redirect ke halaman login
          window.location.href = '/admin';
          throw new Error('Sesi habis, silakan login kembali');
        }
      }
      
      console.log('User yang terautentikasi:', user?.email);
      
      // Pastikan bucket tersedia
      await ensureBucketExists();

      const fileExt = file.name.split('.').pop()?.toLowerCase();
      const fileName = `${Date.now()}-${Math.random().toString(36).substring(2, 9)}.${fileExt}`;
      const filePath = `blog-featured/${fileName}`;

      console.log('Mengunggah ke Supabase:', filePath);
      
      // Cek ketersediaan bucket
      const { data: buckets, error: bucketError } = await supabase.storage.listBuckets();
      
      if (bucketError) {
        console.error('Error mengakses penyimpanan Supabase:', bucketError);
        throw new Error(`Tidak dapat mengakses penyimpanan: ${bucketError.message}`);
      }
      
      console.log('Bucket yang tersedia:', buckets?.map(b => b.name));

      // Upload file ke Supabase Storage
      const { data: uploadData, error: uploadError } = await supabase.storage
        .from('blog-images')
        .upload(filePath, file, {
          cacheControl: '3600',
          upsert: true,
          contentType: file.type
        });

      if (uploadError) {
        console.error('Error saat mengunggah:', uploadError);
        throw new Error(`Gagal mengunggah file: ${uploadError.message}`);
      }

      console.log('Berhasil diunggah:', uploadData);

      // Dapatkan URL publik
      const { data: { publicUrl } } = supabase.storage
        .from('blog-images')
        .getPublicUrl(filePath);

      console.log('URL publik yang dihasilkan:', publicUrl);

      if (!publicUrl) {
        throw new Error('Gagal menghasilkan URL publik untuk gambar');
      }

      // Perbarui form dengan URL gambar baru
      setFormData(prev => ({
        ...prev,
        featured_image: publicUrl
      }));

      toast({
        title: "Berhasil",
        description: "Gambar berhasil diunggah",
      });
    } catch (error) {
      console.error('Error dalam handleImageUpload:', error);
      const errorMessage = error instanceof Error ? error.message : 'Terjadi kesalahan yang tidak diketahui';
      toast({
        title: "Error",
        description: `Gagal mengunggah gambar: ${errorMessage}`,
        variant: "destructive",
      });
    } finally {
      setIsUploading(false);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      // Validasi ukuran file sebelum mengunggah
      const maxSize = 5 * 1024 * 1024; // 5MB
      if (file.size > maxSize) {
        const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
        toast({
          title: "Error",
          description: `Ukuran file terlalu besar: ${fileSizeMB}MB. Maksimal 5MB`,
          variant: "destructive",
        });
        e.target.value = '';
        return;
      }
      handleImageUpload(file);
    }
    // Reset input untuk memungkinkan memilih file yang sama lagi
    e.target.value = '';
  };

  const removeFeaturedImage = () => {
    setFormData(prev => ({
      ...prev,
      featured_image: ''
    }));
  };

  const generateSlug = (title: string) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9\s-]/g, '')
      .replace(/\s+/g, '-')
      .replace(/-+/g, '-')
      .trim();
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.title || !formData.content) {
      toast({
        title: "Error",
        description: "Judul dan konten wajib diisi",
        variant: "destructive",
      });
      return;
    }

    try {
      const slug = formData.slug || generateSlug(formData.title);
      const tagsArray = formData.tags.split(',').map(tag => tag.trim()).filter(tag => tag);
      
      const postData = {
        title: formData.title,
        slug,
        content: formData.content,
        excerpt: formData.excerpt,
        published: formData.published,
        published_at: formData.published ? new Date().toISOString() : null,
        tags: tagsArray,
        featured_image: formData.featured_image
      };

      if (editingPost) {
        const { error } = await supabase
          .from('blog_posts')
          .update(postData)
          .eq('id', editingPost.id);
        
        if (error) throw error;
        
        toast({
          title: "Berhasil",
          description: "Artikel berhasil diperbarui",
        });
      } else {
        const { error } = await supabase
          .from('blog_posts')
          .insert([postData]);
        
        if (error) throw error;
        
        toast({
          title: "Berhasil",
          description: "Artikel berhasil dibuat",
        });
      }

      fetchPosts();
      setDialogOpen(false);
      resetForm();
    } catch (error) {
      console.error('Error saving post:', error);
      toast({
        title: "Error",
        description: "Gagal menyimpan artikel",
        variant: "destructive",
      });
    }
  };

  const handleEdit = (post: BlogPost) => {
    setEditingPost(post);
    setFormData({
      title: post.title,
      slug: post.slug,
      content: post.content,
      excerpt: post.excerpt || '',
      published: post.published,
      tags: post.tags?.join(', ') || '',
      featured_image: post.featured_image || ''
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id: string) => {
    try {
      const { error } = await supabase
        .from('blog_posts')
        .delete()
        .eq('id', id);
      
      if (error) throw error;
      
      fetchPosts();
      toast({
        title: "Berhasil",
        description: "Artikel berhasil dihapus",
      });
    } catch (error) {
      console.error('Error deleting post:', error);
      toast({
        title: "Error",
        description: "Gagal menghapus artikel",
        variant: "destructive",
      });
    }
  };

  const handleFormSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    handleLogin();
  };

  if (!isAuthenticated) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50/80 via-background to-purple-50/80 flex items-center justify-center p-4">
        <Card className="w-full max-w-md">
          <CardHeader className="text-center space-y-2">
            <CardTitle className="text-2xl">Admin Login</CardTitle>
            <CardDescription>Masuk ke panel admin blog</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleFormSubmit} className="space-y-4">
              <div>
                <Label htmlFor="username">Email</Label>
                <Input
                  id="username"
                  type="email"
                  value={credentials.username}
                  onChange={(e) => setCredentials({ ...credentials, username: e.target.value })}
                  placeholder="Masukkan email Anda"
                  required
                />
              </div>
              <div>
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  value={credentials.password}
                  onChange={(e) => setCredentials({ ...credentials, password: e.target.value })}
                  placeholder="Masukkan password Anda"
                  required
                />
              </div>
              <Button type="submit" className="w-full">
                Masuk
              </Button>
            </form>
            <div className="mt-4 text-center">
              <Link to="/" className="text-sm text-blue-600 hover:underline">
                Kembali
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50/80 via-background to-purple-50/80 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Memuat artikel...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50/80 via-background to-purple-50/80">
      {/* Header */}
      <header className="sticky top-0 z-50 bg-white/70 backdrop-blur-md border-b border-gray-200 shadow-sm">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-2 text-blue-600 hover:text-blue-700 transition-colors">
              <ArrowLeft className="w-5 h-5" />
              <span className="font-medium">Kembali</span>
            </Link>
            <h1 className="text-2xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
              Admin Panel
            </h1>
            <Button variant="outline" onClick={handleLogout}>
              Logout
            </Button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <Tabs defaultValue="blog" className="w-full">
          <TabsList className="inline-flex h-12 items-center justify-center rounded-xl bg-gradient-to-r from-gray-50 to-gray-100 p-1 mb-8 w-full shadow-inner border border-gray-200 dark:from-gray-800 dark:to-gray-900 dark:border-gray-700">
            <TabsTrigger 
              value="blog" 
              className="relative flex-1 h-10 mx-1 rounded-lg text-sm font-medium transition-all duration-200 ease-in-out
                data-[state=active]:bg-gradient-to-r data-[state=active]:from-blue-500 data-[state=active]:to-purple-600
                data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:shadow-blue-500/20
                hover:bg-white/50 dark:hover:bg-white/10"
            >
              <div className="flex items-center gap-2 px-4">
                <MessageSquare className="h-4 w-4" />
                <span>Artikel Blog</span>
                <span className="ml-1.5 px-2 py-0.5 text-xs font-semibold rounded-full bg-white/20 text-white">
                  {posts.length}
                </span>
              </div>
            </TabsTrigger>
            <TabsTrigger 
              value="popup" 
              className="relative flex-1 h-10 mx-1 rounded-lg text-sm font-medium transition-all duration-200 ease-in-out
                data-[state=active]:bg-gradient-to-r data-[state=active]:from-purple-500 data-[state=active]:to-pink-600
                data-[state=active]:text-white data-[state=active]:shadow-lg data-[state=active]:shadow-purple-500/20
                hover:bg-white/50 dark:hover:bg-white/10"
            >
              <div className="flex items-center gap-2 px-4">
                <MessageSquare className="h-4 w-4" />
                <span>Manajemen Popup</span>
                <span className="ml-1.5 px-2 py-0.5 text-xs font-semibold rounded-full bg-white/20 text-white">
                  {popups.length}
                </span>
              </div>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="blog" className="mt-0">
            <div className="flex justify-between items-center">
              <div>
                <h2 className="text-3xl font-bold text-gray-900">Manajemen Artikel</h2>
                <p className="text-gray-600 mt-2">Kelola artikel blog Perkalian.com</p>
              </div>
              
              <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                <DialogTrigger asChild>
                  <Button onClick={resetForm} className="flex items-center gap-2">
                    <Plus className="w-4 h-4" />
                    Artikel Baru
                  </Button>
                </DialogTrigger>
                <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
                  <DialogHeader>
                    <DialogTitle>
                      {editingPost ? 'Edit Artikel' : 'Buat Artikel Baru'}
                    </DialogTitle>
                  </DialogHeader>
                  
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label htmlFor="title">Judul Artikel *</Label>
                        <Input
                          id="title"
                          value={formData.title}
                          onChange={(e) => {
                            setFormData(prev => ({ 
                              ...prev, 
                              title: e.target.value,
                              slug: generateSlug(e.target.value)
                            }));
                          }}
                          placeholder="Masukkan judul artikel"
                          required
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="slug">Slug</Label>
                        <Input
                          id="slug"
                          value={formData.slug}
                          onChange={(e) => setFormData(prev => ({ ...prev, slug: e.target.value }))}
                          placeholder="url-artikel-ini"
                        />
                      </div>
                    </div>

                    <div className="space-y-2">
                      <Label htmlFor="featured_image">Gambar Utama</Label>
                      <div className="flex gap-2">
                        <Input
                          id="featured_image"
                          value={formData.featured_image}
                          onChange={(e) => setFormData(prev => ({ ...prev, featured_image: e.target.value }))}
                          placeholder="https://example.com/image.jpg"
                          className="flex-1"
                        />
                        <div className="relative">
                          <input
                            type="file"
                            id="file-upload"
                            accept="image/*"
                            onChange={handleFileChange}
                            disabled={isUploading}
                            className="hidden"
                          />
                          <label
                            htmlFor="file-upload"
                            className={`inline-flex items-center justify-center px-4 py-2 border border-gray-300 rounded-md shadow-sm text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${isUploading ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer'}`}
                          >
                            <Upload className="w-4 h-4 mr-2" />
                            {isUploading ? 'Mengunggah...' : 'Unggah'}
                          </label>
                        </div>
                      </div>
                      {formData.featured_image && (
                        <div className="mt-2 relative">
                          <div className="relative inline-block">
                            <img
                              src={formData.featured_image}
                              alt="Preview"
                              className="h-32 w-auto object-cover rounded-md border"
                            />
                            <button
                              type="button"
                              onClick={removeFeaturedImage}
                              className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full p-1 hover:bg-red-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                            >
                              <X className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      )}
                    </div>

                    <div>
                      <Label htmlFor="excerpt">Ringkasan</Label>
                      <Textarea
                        id="excerpt"
                        value={formData.excerpt}
                        onChange={(e) => setFormData(prev => ({ ...prev, excerpt: e.target.value }))}
                        placeholder="Ringkasan singkat artikel ini"
                        rows={3}
                      />
                    </div>

                    <div>
                      <Label htmlFor="tags">Tags (pisahkan dengan koma)</Label>
                      <Input
                        id="tags"
                        value={formData.tags}
                        onChange={(e) => setFormData(prev => ({ ...prev, tags: e.target.value }))}
                        placeholder="matematika, pendidikan, perkalian"
                      />
                    </div>

                    <div>
                      <Label>Konten Artikel *</Label>
                      <RichTextEditor
                        value={formData.content}
                        onChange={(value) => setFormData(prev => ({ ...prev, content: value }))}
                      />
                    </div>

                    <div className="flex items-center space-x-2">
                      <Switch
                        id="published"
                        checked={formData.published}
                        onCheckedChange={(checked) => setFormData(prev => ({ ...prev, published: checked }))}
                      />
                      <Label htmlFor="published">Publikasikan artikel</Label>
                    </div>

                    <div className="flex justify-end gap-3">
                      <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                        Batal
                      </Button>
                      <Button type="submit">
                        {editingPost ? 'Perbarui' : 'Simpan'} Artikel
                      </Button>
                    </div>
                  </form>
                </DialogContent>
              </Dialog>
            </div>

            {/* Articles List */}
            <div className="grid gap-4">
              {posts.map((post) => (
                <Card key={post.id} className="bg-white/80 backdrop-blur-sm">
                  <CardContent className="p-6">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                      <div className="flex-1">
                        <div className="flex items-center gap-3 mb-2">
                          <h3 className="text-lg font-semibold">{post.title}</h3>
                          <Badge variant={post.published ? "default" : "secondary"}>
                            {post.published ? "Published" : "Draft"}
                          </Badge>
                        </div>
                        
                        <p className="text-gray-600 text-sm mb-3 line-clamp-2">
                          {post.excerpt}
                        </p>
                        
                        <div className="flex flex-wrap items-center gap-4 text-xs text-gray-500">
                          <div className="flex items-center gap-1">
                            <Calendar className="w-3 h-3" />
                            {format(new Date(post.created_at), 'dd MMM yyyy')}
                          </div>
                          <div className="flex items-center gap-1">
                            <Eye className="w-3 h-3" />
                            {post.view_count || 0} views
                          </div>
                          {post.tags && post.tags.length > 0 && (
                            <div className="flex gap-1">
                              {post.tags.slice(0, 2).map(tag => (
                                <Badge key={tag} variant="outline" className="text-xs">
                                  {tag}
                                </Badge>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => handleEdit(post)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button variant="destructive" size="sm">
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Hapus Artikel</AlertDialogTitle>
                              <AlertDialogDescription>
                                Apakah Anda yakin ingin menghapus artikel "{post.title}"? 
                                Tindakan ini tidak dapat dibatalkan.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Batal</AlertDialogCancel>
                              <AlertDialogAction onClick={() => handleDelete(post.id)}>
                                Hapus
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>

            {posts.length === 0 && (
              <div className="text-center py-12">
                <h3 className="text-xl font-semibold text-gray-600 mb-2">Belum ada artikel</h3>
                <p className="text-gray-500 mb-4">Mulai dengan membuat artikel pertama Anda</p>
                <Button onClick={() => setDialogOpen(true)}>
                  <Plus className="w-4 h-4 mr-2" />
                  Buat Artikel Pertama
                </Button>
              </div>
            )}
          </TabsContent>

          <TabsContent value="popup" className="mt-6">
            <Card>
              <CardHeader>
                <CardTitle className="text-xl">Kelola Popup</CardTitle>
                <CardDescription>
                  Kelola popup yang akan ditampilkan di halaman utama
                </CardDescription>
              </CardHeader>
              <CardContent>
                <PopupManager />
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </main>
    </div>
  );
};

export default AdminBlog;